import formatMetricScoreType from "@Containers/app/stat-cards/tooltips/format-metric-type";
import { MetricGroup } from "@Containers/shared/progress/utils";
import useI18n from "@i18n";
import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

interface ICustomTooltip {
  color: string;
  active?: boolean;
  decimals?: number;
  metricType?: string;
  showUnit?: boolean;
  payload?: {
    value: number;
    payload: MetricGroup;
    dataKey?: string;
  }[];
}

const CustomTooltip = ({ color, active, payload, decimals = 2, metricType, showUnit = false }: ICustomTooltip) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const actualData =
    payload.find(p => p.dataKey === "tooltipScore" && p.value !== null && p.value !== undefined) ||
    payload.find(p => p.dataKey === "score" && p.value !== null && p.value !== undefined);

  if (!actualData) {
    return null;
  }

  const rawValue = actualData.value;
  const formattedValue = metricType ? formatMetricScoreType(metricType, rawValue) : rawValue.toFixed(decimals);

  let unit = "";
  if (showUnit) {
    if (actualData.payload.unit) {
      unit = ` ${i18n(actualData.payload.unit)}`;
    } else if (metricType) {
      const metricKey = metricType.toLowerCase();
      const unitKey = `metric.${metricKey}.long`;
      const translatedUnit = i18n(unitKey);

      if (translatedUnit && translatedUnit !== unitKey) {
        unit = ` ${translatedUnit}`;
      }
    }
  }

  const displayValue = `${formattedValue}${unit}`;

  return (
    <Stack
      sx={{
        pointerEvents: "none",
        ...(!isMobile
          ? {
              borderRadius: "10px",
              border: "1px solid",
              borderColor: color,
              backgroundColor: `${theme.palette.dsSurface.v100}90`,
              backdropFilter: `blur(${theme.spacing(2)})`,
              padding: theme.spacing(1),
            }
          : {
              backgroundColor: `${theme.palette.dsSurface.v100}80`,
              backdropFilter: `blur(${theme.spacing(2.5)})`,
              padding: theme.spacing(0.5),
            }),
      }}
    >
      <Typography variant="textBase500" color={theme.palette.dsNeutral.v300}>
        {actualData.payload.name}
      </Typography>
      <Typography
        variant="textBase500"
        sx={{
          color: color,
        }}
      >
        {displayValue}
      </Typography>
    </Stack>
  );
};

export default CustomTooltip;
